import { PlacedPiece, DEFAULT_CONFIG, getPieceCells } from '@game/engine';
import styles from './CurrencyDisplay.module.css';

interface CurrencyDisplayProps {
  currency: number;
  maxCurrency: number;
  pieces: PlacedPiece[];
  tax: number;
  rent: number;
}

export function CurrencyDisplay({ currency, maxCurrency, pieces, tax, rent }: CurrencyDisplayProps) {
  // Calculate total generation rate
  let totalGeneration = 0;

  // Build position map for O(1) lookup
  const piecesByPosition = new Map<string, PlacedPiece>();
  pieces.forEach(p => {
    getPieceCells(p).forEach(cell => {
      piecesByPosition.set(`${cell.x},${cell.y}`, p);
    });
  });

  const neighborOffsets = [
    { dx: 0, dy: -1 },   // Top
    { dx: -1, dy: 0 },   // Left
    { dx: 1, dy: 0 },    // Right
    { dx: 0, dy: 1 },    // Bottom
  ];

  for (const piece of pieces) {
    const config = DEFAULT_CONFIG.tileConfigs[piece.type];

    // Only generators produce currency and only when active
    if (config.generationRate > 0 && piece.state === 'active') {
      let totalMultiplier = 0;
      const cells = getPieceCells(piece);
      const countedEnhancers = new Set<string>();

      // Calculate enhancement from adjacent active enhancers
      for (const cell of cells) {
        for (const offset of neighborOffsets) {
          const key = `${cell.x + offset.dx},${cell.y + offset.dy}`;
          const neighborPiece = piecesByPosition.get(key);

          if (
            neighborPiece &&
            !countedEnhancers.has(neighborPiece.id) &&
            neighborPiece.type === 'I' &&
            neighborPiece.state === 'active'
          ) {
            const nConfig = DEFAULT_CONFIG.tileConfigs[neighborPiece.type];
            if (nConfig.enhancerMultiplier && nConfig.enhancerMultiplier > 0) {
              totalMultiplier += nConfig.enhancerMultiplier;
              countedEnhancers.add(neighborPiece.id);
            }
          }
        }
      }

      // Then, if piece is in a T-network, check all other active I pieces in the network
      if (piece.tNetworkId) {
        for (const networkPiece of pieces) {
          if (
            networkPiece.type === 'I' &&
            networkPiece.state === 'active' &&
            networkPiece.tNetworkId === piece.tNetworkId &&
            !countedEnhancers.has(networkPiece.id)
          ) {
            const nConfig = DEFAULT_CONFIG.tileConfigs[networkPiece.type];
            if (nConfig.enhancerMultiplier && nConfig.enhancerMultiplier > 0) {
              totalMultiplier += nConfig.enhancerMultiplier;
              countedEnhancers.add(networkPiece.id);
            }
          }
        }
      }

      const effectiveRate = Math.max(0, config.generationRate * (1 + totalMultiplier) - tax);
      totalGeneration += effectiveRate;
    }
  }

  const net = totalGeneration - rent;
  const fmt = (n: number) => n % 1 === 0 ? String(n) : n.toFixed(1);

  return (
    <div className={styles.currencyDisplay}>
<span className={styles.amount}>{Math.floor(currency)}💰/{maxCurrency}💰</span>
      {rent > 0 ? (
        <span className={styles.generation}> ({fmt(totalGeneration)}💰 - {fmt(rent)}🏠 = {fmt(net)}💰 / sec)</span>
      ) : (
        <span className={styles.generation}> ({fmt(totalGeneration)}💰 / sec)</span>
      )}
    </div>
  );
}
